"use client";

import NoImage from "@/components/illustratores/NoImage";
import ImageLoading from "@/components/ImageLoading";
import useSetSearchParams from '@/hooks/useSetSearchParams';
import useSizeController from '@/hooks/useSizeController';
import dynamic from "next/dynamic";
import { useRouter } from 'next/navigation';
import { useEffect, useState } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/Carousel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGalleryThumbnails, faImageLandscape } from '@fortawesome/pro-regular-svg-icons';

interface ImageData {
  id: string,
  type: string,
  url: string
}

const DynamicGalleryModal = dynamic(() => import('./GalleryModal'), {
  ssr: false,
})

export default function Gallery({ images, title, }: { images: ImageData[]; title?: string; hotelId: string; }) {
  const [showModal, setShowModal] = useState(false);


  const { addQuey, params } = useSetSearchParams()

  useEffect(() => {
    if (showModal && typeof window !== 'undefined') {
      document.documentElement.classList.add("overflow-hidden");
      document.body.classList.add("overflow-hidden");
      isSmall && addQuey(formattedIdentifier)
    } else if (typeof window !== 'undefined') {
      document.documentElement.classList.remove("overflow-hidden");
      document.body.classList.remove("overflow-hidden");
    }
  }, [showModal]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const { back } = useRouter()

  const { isSmall } = useSizeController()

  const formattedIdentifier = `modal-gallery`

  const queryIdentifier = params[formattedIdentifier]

  useEffect(() => {
    if (!queryIdentifier) {
      setShowModal(false)
    }
  }, [queryIdentifier])


  return (
    <div className='relative '>

      {images && <div
        className='w-full pt-4  aspect-[1.7/1] lg:aspect-auto lg:h-[460px]  relative h-full
         grid md:grid-cols-[1fr,_0.5fr] lg:grid-cols-[0.3fr,_1fr,_0.3fr] gap-2'>
        <div className='grid-rows-2 max-h-full lg:py-10 relative  gap-2 hidden lg:grid'>
          <div className='w-full h-full lg:max-h-[34vh]  rounded-3xl flex items-center justify-center bg-gray-200 relative overflow-hidden '>
            {images[2]?.url ? (
              <ImageLoading quality={60} src={images[2].url}
                style={{
                  viewTransitionName: images[2]?.id ? `hotel-image-${images[2]?.id}` : undefined, // ✅ Use hotel ID for consistency
                }}
                width={420} height={300} alt="Planingo" sizes="(max-width: 640px) 150, 300px"
                className="h-full absolute max-h-full w-full rounded-3xl object-cover sm:h-full " />
            ) : (
              <NoImage />
            )}
          </div>
          <div className='w-full  rounded-3xl lg:max-h-[34vh] flex items-center justify-center bg-gray-200 relative  overflow-hidden '>
            {images[3]?.url ? (
              <ImageLoading
                style={{
                  viewTransitionName: images[3]?.id ? `hotel-image-${images[3]?.id}` : undefined, // ✅ Use hotel ID for consistency
                }}
                sizes="(max-width: 640px) 150, 300px" quality={60} src={images[3].url} width={420} height={300} alt="Planingo"
                className="h-full w-full absolute rounded-3xl object-cover sm:h-full " />
            ) : (
              <NoImage />
            )}
          </div>
        </div>
        <div className='max-h-full overflow-hidden aspect-[1.41/1] md:aspect-auto h-full w-full'>
          <div className='w-full h-full rounded-2xl md:rounded-3xl flex items-center justify-center bg-gray-200 relative overflow-hidden '>
            <Carousel className="w-full h-full  relative">
              <div className='p-2 px-3 w-fit absolute bottom-2 left-2 z-20 flex items-center gap-1 
                        rounded-xl bg-black/50 backdrop-blur-sm text-white'
              >
                <FontAwesomeIcon icon={faImageLandscape} />
                {images.length}
              </div>
              <CarouselContent>
                {images.map((image) => (
                  <CarouselItem
                    key={image.id}
                    className={`cursor-pointer h-full `}>
                    {image.url ? (
                      <ImageLoading width={800} height={500}
                        sizes="(max-width: 640px) 600px, 800px"
                        priority
                        onClick={() => {
                          setShowModal(true)
                        }}
                        quality={75} src={image.url} alt="Planingo"
                        className="h-full w-full max-h-full min-h-full sm:min-h-[480px] object-cover sm:h-full  " />
                    ) : (
                      <NoImage />
                    )}
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className='!left-2 !z-20' />
              <CarouselNext className='!right-2 !z-20' />
            </Carousel>

          </div>
        </div>

        <div className='grid-rows-2 max-h-full lg:py-10 relative  gap-2 hidden md:grid'>
          <div className='w-full h-full lg:max-h-[34vh]  rounded-3xl flex items-center justify-center bg-gray-200 relative overflow-hidden '>
            {images[4]?.url ? (
              <ImageLoading quality={60} src={images[4].url}
                width={420} height={300} alt="Planingo" sizes="(max-width: 640px) 150, 300px"
                className="h-full absolute max-h-full w-full rounded-3xl object-cover sm:h-full " />
            ) : (
              <NoImage />
            )}
          </div>
          <div className='w-full  rounded-3xl lg:max-h-[34vh] flex items-center justify-center bg-gray-200 relative  overflow-hidden '>
            {images[5]?.url ? (
              <ImageLoading
                sizes="(max-width: 640px) 150, 300px" quality={60} src={images[5].url} width={420} height={300} alt="Planingo"
                className="h-full w-full absolute rounded-3xl object-cover sm:h-full " />
            ) : (
              <NoImage />
            )}
          </div>
        </div>

      </div>}

      {showModal && <DynamicGalleryModal images={images} title={title}
        showModal={showModal}
        onClose={() => {
          setShowModal(false)
        }} />}
    </div>
  );
};

